import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { invalidatePlanningOverride } from './invalidateForecastViews';
import type {
  ForecastFactorRow,
  ListForecastFactorsResponse,
  PreviewForecastFactorInput,
  PreviewForecastFactorResponse,
} from '@erp/rpc';

export type {
  ForecastFactorRow,
  ListForecastFactorsResponse,
  PreviewForecastFactorInput,
  PreviewForecastFactorResponse,
};

export interface UseForecastFactorsInput {
  /** Null disables the query (rows without a warehouse have no series). */
  warehouseId: string | null;
  /** ProductV3 key (the SKU); null disables the query too. */
  productV3Key?: string | null;
}

// Every growth/shrinkage factor (active + history) for one SKU x warehouse,
// plus the newest active one. ProductV3 only: there is no legacy v2 lookup.
export function useForecastFactors(
  input: UseForecastFactorsInput,
  enabled = true,
) {
  return useQuery<ListForecastFactorsResponse>({
    ...orpc.procurement.forecastFactors.list.queryOptions({
      input: {
        // Never sent when null; `enabled` gates the fetch below.
        warehouseId: input.warehouseId ?? '',
        productV3Key: input.productV3Key ?? '',
      },
    }),
    enabled: enabled && !!input.warehouseId && !!input.productV3Key,
  });
}

export function useCreateForecastFactor() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.forecastFactors.create.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'factor'),
    }),
  );
}

export function useUpdateForecastFactor() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.forecastFactors.update.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'factor'),
    }),
  );
}

export function useRevokeForecastFactor() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.forecastFactors.revoke.mutationOptions({
      onSuccess: () => invalidatePlanningOverride(queryClient, 'factor'),
    }),
  );
}

// Before/after levels of a candidate factor, computed server-side in memory.
// Null input disables the query (the form is incomplete); the placeholder is
// never sent.
export function useForecastFactorPreview(
  input: PreviewForecastFactorInput | null,
) {
  return useQuery<PreviewForecastFactorResponse>({
    ...orpc.procurement.forecastFactors.preview.queryOptions({
      input: input ?? { warehouseId: '', productV3Key: '', factor: 2 },
    }),
    enabled: input !== null,
    staleTime: 15_000,
  });
}
